/**
 * Feature Flag Service
 *
 * Provides feature flag functionality for gradual rollout of new features.
 * Uses Supabase for remote configuration with local caching to reduce queries.
 *
 * Key features:
 * - Remote flag fetching from Supabase
 * - Consistent hashing for percentage-based rollout
 * - Per-user override support
 * - Local cache with TTL (5 minutes)
 * - Graceful degradation when Supabase is unavailable
 */

import { supabase } from '@/integrations/supabase/client';

// ============================================================================
// Types
// ============================================================================

/**
 * Feature flag configuration from the database
 */
export interface FeatureFlag {
  name: string;
  description: string | null;
  is_enabled: boolean;
  rollout_percentage: number;
  config: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

/**
 * User-specific feature flag override
 */
export interface UserFeatureFlag {
  id: number;
  user_id: string;
  flag_name: string;
  is_enabled: boolean | null;
  variant: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * Result of checking a feature flag for a user
 */
export interface FeatureFlagResult {
  enabled: boolean;
  variant?: string;
  source: 'global' | 'user-override' | 'rollout' | 'default';
}

// ============================================================================
// Cache Configuration
// ============================================================================

const CACHE_TTL = 5 * 60 * 1000; // 5 minutes in milliseconds

interface CacheEntry {
  flag: FeatureFlag;
  timestamp: number;
}

// Simple in-memory cache for feature flags
const flagCache = new Map<string, CacheEntry>();

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Generates a consistent hash from a string (0-99)
 * Uses a simple string hashing algorithm to ensure the same input
 * always produces the same output
 *
 * @param input - String to hash (typically a user ID)
 * @returns A number between 0 and 99
 */
function consistentHash(input: string): number {
  let hash = 0;
  for (let i = 0; i < input.length; i++) {
    const char = input.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash) % 100;
}

/**
 * Checks if a cache entry is still valid based on TTL
 *
 * @param entry - Cache entry to check
 * @returns True if the entry is still valid
 */
function isCacheEntryValid(entry: CacheEntry | undefined): boolean {
  if (!entry) return false;
  return Date.now() - entry.timestamp < CACHE_TTL;
}

/**
 * Gets a value from cache or returns undefined if not found/expired
 *
 * @param flagName - Name of the flag to retrieve from cache
 * @returns The cached flag or undefined
 */
function getCachedFlag(flagName: string): FeatureFlag | undefined {
  const entry = flagCache.get(flagName);
  if (isCacheEntryValid(entry)) {
    return entry.flag;
  }
  // Remove expired entry
  if (entry) {
    flagCache.delete(flagName);
  }
  return undefined;
}

/**
 * Stores a flag in the cache
 *
 * @param flag - Flag to cache
 */
function setCachedFlag(flag: FeatureFlag): void {
  flagCache.set(flag.name, {
    flag,
    timestamp: Date.now(),
  });
}

/**
 * Clears the cache (useful for testing or force-refresh)
 */
export function clearFlagCache(): void {
  flagCache.clear();
}

// ============================================================================
// Core Feature Flag Functions
// ============================================================================

/**
 * Fetches a single feature flag from Supabase
 * Uses cache to reduce database queries
 *
 * @param name - Name of the feature flag to fetch
 * @returns The feature flag or null if not found
 */
export async function getFeatureFlag(name: string): Promise<FeatureFlag | null> {
  // Check cache first
  const cached = getCachedFlag(name);
  if (cached) {
    return cached;
  }

  try {
    const { data, error } = await supabase
      .from('feature_flags')
      .select('*')
      .eq('name', name)
      .maybeSingle();

    if (error) {
      console.error(`Error fetching feature flag "${name}":`, error);
      return null;
    }

    if (data) {
      // Cache the result
      setCachedFlag(data as FeatureFlag);
      return data as FeatureFlag;
    }

    return null;
  } catch (err) {
    console.error(`Unexpected error fetching feature flag "${name}":`, err);
    return null;
  }
}

/**
 * Fetches all feature flags from Supabase
 * Useful for bulk fetching to reduce individual queries
 *
 * @returns Array of all feature flags
 */
export async function getAllFeatureFlags(): Promise<FeatureFlag[]> {
  try {
    const { data, error } = await supabase
      .from('feature_flags')
      .select('*')
      .order('name');

    if (error) {
      console.error('Error fetching all feature flags:', error);
      return [];
    }

    const flags = (data || []) as FeatureFlag[];

    // Cache all flags
    flags.forEach(flag => setCachedFlag(flag));

    return flags;
  } catch (err) {
    console.error('Unexpected error fetching all feature flags:', err);
    return [];
  }
}

/**
 * Checks if a specific user is in the rollout percentage for a flag
 * Uses consistent hashing to ensure the same user always gets the same result
 *
 * @param userId - The user's ID (UUID string)
 * @param flagName - Name of the feature flag
 * @returns True if the user is in the rollout bucket
 */
export async function isUserInRollout(
  userId: string,
  flagName: string
): Promise<boolean> {
  try {
    // First, check if there's a user-specific override
    const { data: userOverride, error: userError } = await supabase
      .from('user_feature_flags')
      .select('*')
      .eq('user_id', userId)
      .eq('flag_name', flagName)
      .maybeSingle();

    if (!userError && userOverride) {
      const override = userOverride as UserFeatureFlag;
      // If override has explicit is_enabled value, use that
      if (override.is_enabled !== null) {
        return override.is_enabled;
      }
    }

    // Get the flag configuration
    const flag = await getFeatureFlag(flagName);

    if (!flag) {
      // Flag doesn't exist, default to false (safe default)
      return false;
    }

    // If flag is fully enabled, everyone gets it
    if (flag.is_enabled) {
      return true;
    }

    // If rollout percentage is 0, no one gets it
    if (flag.rollout_percentage === 0) {
      return false;
    }

    // Calculate consistent hash for user_id
    const userHash = consistentHash(userId);

    // User is in rollout if their hash is less than the rollout percentage
    return userHash < flag.rollout_percentage;
  } catch (err) {
    console.error(`Error checking rollout for user "${userId}" and flag "${flagName}":`, err);
    // Default to false on error (safe default)
    return false;
  }
}

/**
 * Gets the complete feature flag result for a user
 * Includes whether the flag is enabled, which variant, and the source of the decision
 *
 * @param userId - The user's ID (UUID string)
 * @param flagName - Name of the feature flag
 * @returns FeatureFlagResult with enabled status, variant, and source
 */
export async function getFeatureFlagForUser(
  userId: string,
  flagName: string
): Promise<FeatureFlagResult> {
  try {
    // Check for user-specific override first
    const { data: userOverride, error: userError } = await supabase
      .from('user_feature_flags')
      .select('*')
      .eq('user_id', userId)
      .eq('flag_name', flagName)
      .maybeSingle();

    if (!userError && userOverride) {
      const override = userOverride as UserFeatureFlag;
      if (override.is_enabled !== null) {
        return {
          enabled: override.is_enabled,
          variant: override.variant || undefined,
          source: 'user-override',
        };
      }
    }

    // Get the flag configuration
    const flag = await getFeatureFlag(flagName);

    if (!flag) {
      // Flag doesn't exist, return disabled with default source
      return {
        enabled: false,
        source: 'default',
      };
    }

    // If flag is fully enabled, everyone gets it
    if (flag.is_enabled) {
      return {
        enabled: true,
        variant: (flag.config.variant as string) || undefined,
        source: 'global',
      };
    }

    // If rollout percentage is 0, no one gets it
    if (flag.rollout_percentage === 0) {
      return {
        enabled: false,
        source: 'default',
      };
    }

    // Calculate consistent hash for user_id
    const userHash = consistentHash(userId);

    // User is in rollout if their hash is less than the rollout percentage
    const enabled = userHash < flag.rollout_percentage;

    return {
      enabled,
      variant: enabled ? (flag.config.variant as string) || undefined : undefined,
      source: enabled ? 'rollout' : 'default',
    };
  } catch (err) {
    console.error(`Error getting feature flag for user "${userId}" and flag "${flagName}":`, err);
    // Default to disabled on error (safe default)
    return {
      enabled: false,
      source: 'default',
    };
  }
}

/**
 * Gets all user-specific feature flag overrides
 *
 * @param userId - The user's ID (UUID string)
 * @returns Array of user feature flags
 */
export async function getUserFlags(userId: string): Promise<UserFeatureFlag[]> {
  try {
    const { data, error } = await supabase
      .from('user_feature_flags')
      .select('*')
      .eq('user_id', userId);

    if (error) {
      console.error(`Error fetching user flags for "${userId}":`, error);
      return [];
    }

    return (data || []) as UserFeatureFlag[];
  } catch (err) {
    console.error(`Unexpected error fetching user flags for "${userId}":`, err);
    return [];
  }
}

/**
 * Sets a user-specific feature flag override
 * Useful for testing or giving specific users early access
 *
 * @param userId - The user's ID (UUID string)
 * @param flagName - Name of the feature flag
 * @param isEnabled - Whether the flag should be enabled for this user
 * @param variant - Optional variant to assign
 * @returns True if successful
 */
export async function setUserFlagOverride(
  userId: string,
  flagName: string,
  isEnabled: boolean | null,
  variant?: string
): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('user_feature_flags')
      .upsert({
        user_id: userId,
        flag_name: flagName,
        is_enabled: isEnabled,
        variant: variant || null,
      }, {
        onConflict: 'user_id,flag_name',
      });

    if (error) {
      console.error(`Error setting user flag override for "${userId}" and flag "${flagName}":`, error);
      return false;
    }

    // Clear cache to force refresh on next check
    flagCache.delete(flagName);

    return true;
  } catch (err) {
    console.error(`Unexpected error setting user flag override for "${userId}" and flag "${flagName}":`, err);
    return false;
  }
}

/**
 * Removes a user-specific feature flag override
 *
 * @param userId - The user's ID (UUID string)
 * @param flagName - Name of the feature flag
 * @returns True if successful
 */
export async function removeUserFlagOverride(
  userId: string,
  flagName: string
): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('user_feature_flags')
      .delete()
      .eq('user_id', userId)
      .eq('flag_name', flagName);

    if (error) {
      console.error(`Error removing user flag override for "${userId}" and flag "${flagName}":`, error);
      return false;
    }

    // Clear cache to force refresh on next check
    flagCache.delete(flagName);

    return true;
  } catch (err) {
    console.error(`Unexpected error removing user flag override for "${userId}" and flag "${flagName}":`, err);
    return false;
  }
}

// ============================================================================
// Admin Functions (for managing flags)
// ============================================================================

/**
 * Creates a new feature flag (admin only)
 *
 * @param flag - Feature flag data to create
 * @returns True if successful
 */
export async function createFeatureFlag(
  flag: Omit<FeatureFlag, 'created_at' | 'updated_at' | 'name'> & { name: string }
): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('feature_flags')
      .insert({
        name: flag.name,
        description: flag.description,
        is_enabled: flag.is_enabled,
        rollout_percentage: flag.rollout_percentage,
        config: flag.config,
      });

    if (error) {
      console.error(`Error creating feature flag "${flag.name}":`, error);
      return false;
    }

    // Cache the new flag
    await getFeatureFlag(flag.name);

    return true;
  } catch (err) {
    console.error(`Unexpected error creating feature flag "${flag.name}":`, err);
    return false;
  }
}

/**
 * Updates an existing feature flag (admin only)
 *
 * @param name - Name of the flag to update
 * @param updates - Partial flag data to update
 * @returns True if successful
 */
export async function updateFeatureFlag(
  name: string,
  updates: Partial<Omit<FeatureFlag, 'name' | 'created_at' | 'updated_at'>>
): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('feature_flags')
      .update(updates)
      .eq('name', name);

    if (error) {
      console.error(`Error updating feature flag "${name}":`, error);
      return false;
    }

    // Clear cache to force refresh
    flagCache.delete(name);

    return true;
  } catch (err) {
    console.error(`Unexpected error updating feature flag "${name}":`, err);
    return false;
  }
}

/**
 * Deletes a feature flag (admin only)
 *
 * @param name - Name of the flag to delete
 * @returns True if successful
 */
export async function deleteFeatureFlag(name: string): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('feature_flags')
      .delete()
      .eq('name', name);

    if (error) {
      console.error(`Error deleting feature flag "${name}":`, error);
      return false;
    }

    // Remove from cache
    flagCache.delete(name);

    return true;
  } catch (err) {
    console.error(`Unexpected error deleting feature flag "${name}":`, err);
    return false;
  }
}
